import {
  activeFinishOf,
  contrastRatio,
  formatMoney,
  THEME_FONTS,
  THEME_MENUS,
  mixColors,
  parseThemeFile,
  themeColors,
  themeContrastWarnings,
  type PlatformSettings,
  type ThemeColorToken,
  type ThemeFile,
} from '@base/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, FileUp, RotateCcw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.ts';
import { can, useMe } from '../../auth.tsx';
import { useLeaveGuard } from '../../components/LeaveGuard.tsx';
import { SearchBox, StatusFilter, ListToolbar } from '../../components/ListFilters.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Field,
  Input,
  Loading,
  NumCell,
  PageHeader,
  Panel,
  Select,
  StatCard,
  Table,
} from '../../components/ui.tsx';
import {
  applyTheme,
  BASE_THEME,
  FACTORY_STYLE,
  PLATFORM_KEY,
  usePlatformSettings,
} from '../../platform.tsx';

/** As cores que a tela deixa editar (o resto do arquivo vem junto, sem mexer). */
const EDITABLE: ThemeColorToken[] = [
  'accent',
  'paper',
  'sheet',
  'ink',
  'muted',
  'sidebar',
  'sidebar-text',
  'sidebar-active',
];

const PX = (from: number, to: number, step = 1) =>
  Array.from({ length: Math.floor((to - from) / step) + 1 }, (_, i) => `${from + i * step}px`);

/**
 * Configuração › Guia visual: o tema do sistema (o arquivo `base-theme`). Mostra as peças com o
 * tema, deixa editar os principais aspectos, importar o arquivo gerado pelo questionário de design
 * e exportar o atual. A tela inteira acompanha a edição (prévia); os outros só veem depois de salvar.
 */
export function DesignPage() {
  const { t } = useTranslation();
  const settings = usePlatformSettings();
  return (
    <>
      <PageHeader
        title={t('nav.design')}
        description={t('design.description')}
        meta={
          settings.data &&
          (settings.data.theme ? (
            <Badge tone="ok">{settings.data.theme.name}</Badge>
          ) : (
            <Badge tone="info">{t('design.usingDefault')}</Badge>
          ))
        }
      />
      {settings.isPending ? (
        <Loading />
      ) : settings.isError ? (
        <ErrorBox error={settings.error} />
      ) : (
        <DesignForm initial={settings.data} />
      )}
    </>
  );
}

function DesignForm({ initial }: { initial: PlatformSettings }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { data: me } = useMe();
  const readOnly = !can(me, 'platform.settings.manage');
  const start = initial.theme ?? BASE_THEME;
  const [theme, setTheme] = useState<ThemeFile>(start);
  const [importMsg, setImportMsg] = useState<{ ok: boolean; text: string; issues?: string[] }>();
  const fileInput = useRef<HTMLInputElement>(null);
  const dirty = JSON.stringify(theme) !== JSON.stringify(start);
  const leave = useLeaveGuard(dirty && !readOnly);
  const warnings = themeContrastWarnings(theme);

  // Prévia: o sistema inteiro acompanha; ao sair sem salvar, volta o tema gravado.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);
  useEffect(
    () => () => {
      const saved = client.getQueryData<PlatformSettings>(PLATFORM_KEY) ?? initial;
      applyTheme(saved.theme, activeFinishOf(saved, FACTORY_STYLE));
    },
    [client, initial],
  );

  const save = useMutation({
    mutationFn: () =>
      api.put<PlatformSettings>('/platform/theme', {
        theme: JSON.stringify(theme) === JSON.stringify(BASE_THEME) ? null : theme,
      }),
    onSuccess: (data) => {
      leave.allowLeave();
      client.setQueryData(PLATFORM_KEY, data);
      setTheme(data.theme ?? BASE_THEME);
    },
  });

  /** Troca uma cor no claro. O destaque leva junto os tons que saem dele. */
  function setColor(token: ThemeColorToken, value: string) {
    const light = { ...themeColors(theme, 'light'), [token]: value };
    if (token === 'accent') {
      let text = value;
      for (let i = 1; i <= 10 && contrastRatio(text, '#ffffff') < 4.5; i++)
        text = mixColors(value, '#000000', i * 0.08);
      Object.assign(light, {
        brand: value,
        'brand-text': text,
        'brand-soft': mixColors(value, '#ffffff', 0.86),
        'sidebar-active': value,
        'on-accent': contrastRatio(value, '#ffffff') >= 3 ? '#ffffff' : '#0e070b',
        'sidebar-active-text': contrastRatio(value, '#ffffff') >= 3 ? '#ffffff' : '#0e070b',
      });
    }
    setTheme({ ...theme, colors: { ...theme.colors, light } });
  }
  const setStyle = <K extends keyof ThemeFile['style']>(k: K, v: ThemeFile['style'][K]) =>
    setTheme({ ...theme, style: { ...theme.style, [k]: v } });

  function importFile(file: File | undefined) {
    setImportMsg(undefined);
    if (!file) return;
    if (file.size > 100_000) {
      setImportMsg({ ok: false, text: t('design.importTooBig') });
      return;
    }
    file.text().then((text) => {
      const r = parseThemeFile(text);
      if (!r.ok) {
        setImportMsg({
          ok: false,
          text: r.reason === 'json' ? t('design.importNotJson') : t('design.importInvalid'),
          issues: r.issues.slice(0, 6),
        });
        return;
      }
      setTheme(r.theme);
      setImportMsg({ ok: true, text: t('design.imported', { name: r.theme.name }) });
    });
  }

  function exportFile() {
    const blob = new Blob(
      [JSON.stringify({ ...theme, exportedAt: new Date().toISOString() }, null, 2)],
      {
        type: 'application/json',
      },
    );
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `tema-${theme.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  const light = themeColors(theme, 'light');
  return (
    <div className="flex flex-col gap-6">
      <ErrorBox error={save.error} showPaths={false} />
      {readOnly && <p className="text-sm text-muted">{t('design.readOnly')}</p>}

      <Panel
        title={t('design.file')}
        action={
          <>
            {!readOnly && (
              <>
                <input
                  ref={fileInput}
                  type="file"
                  accept="application/json,.json"
                  className="sr-only"
                  tabIndex={-1}
                  onChange={(e) => {
                    importFile(e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
                <Button variant="secondary" onClick={() => fileInput.current?.click()}>
                  <FileUp size={16} aria-hidden />
                  {t('design.import')}
                </Button>
              </>
            )}
            <Button variant="secondary" onClick={exportFile}>
              <Download size={16} aria-hidden />
              {t('design.export')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted">{t('design.fileHint')}</p>
        {importMsg && (
          <div className={`text-sm ${importMsg.ok ? 'text-ok' : 'text-danger'}`} role="status">
            <p>{importMsg.text}</p>
            {importMsg.issues && importMsg.issues.length > 0 && (
              <ul className="mt-1 ml-5 list-disc font-mono text-xs">
                {importMsg.issues.map((i) => (
                  <li key={i}>{i}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        {warnings.length > 0 && (
          <div className="rounded-md border border-danger p-3 text-sm" role="status">
            <p className="font-medium text-danger">{t('design.contrastTitle')}</p>
            <ul className="mt-1 ml-5 list-disc text-muted">
              {warnings.slice(0, 6).map((w) => (
                <li key={`${w.mode}-${w.text}-${w.background}`}>
                  {t('design.contrastItem', {
                    mode: t(`design.mode.${w.mode}`),
                    text: w.text,
                    background: w.background,
                    ratio: w.ratio.toLocaleString(),
                    minimum: w.minimum,
                  })}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Panel>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
        <div className="flex flex-col gap-6">
          <Panel title={t('design.colors')}>
            <p className="text-sm text-muted">{t('design.colorsHint')}</p>
            <div className="grid gap-4 sm:grid-cols-2">
              {EDITABLE.map((token) => (
                <Field key={token} label={t(`design.color.${token}`)}>
                  <span className="flex items-center gap-2">
                    <input
                      type="color"
                      aria-label={t(`design.color.${token}`)}
                      disabled={readOnly}
                      value={light[token]}
                      onChange={(e) => setColor(token, e.target.value)}
                      className="h-9 w-12 cursor-pointer rounded border border-control-border bg-sheet"
                    />
                    <span className="font-mono text-xs">{light[token].toUpperCase()}</span>
                  </span>
                </Field>
              ))}
            </div>
          </Panel>
          <Panel title={t('design.shape')}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('design.radius')}>
                <Select
                  value={theme.radius.sm}
                  disabled={readOnly}
                  onChange={(e) => {
                    const sm = e.target.value;
                    const md = `${Math.min(16, parseInt(sm, 10) + 2)}px`;
                    setTheme({ ...theme, radius: { sm, md } });
                  }}
                >
                  {PX(0, 12, 2).map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('design.controlHeight')}>
                <Select
                  value={theme.controlHeight}
                  disabled={readOnly}
                  onChange={(e) => setTheme({ ...theme, controlHeight: e.target.value })}
                >
                  {PX(32, 44, 2).map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('design.logoHeight')}>
                <Select
                  value={theme.logoHeight}
                  disabled={readOnly}
                  onChange={(e) => setTheme({ ...theme, logoHeight: e.target.value })}
                >
                  {PX(24, 56, 4).map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('design.titleSize')}>
                <Select
                  value={theme.pageTitle.size}
                  disabled={readOnly}
                  onChange={(e) =>
                    setTheme({ ...theme, pageTitle: { ...theme.pageTitle, size: e.target.value } })
                  }
                >
                  {PX(20, 32, 2).map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('design.panel')}>
                <Select
                  value={theme.style.panel}
                  disabled={readOnly}
                  onChange={(e) => setStyle('panel', e.target.value as 'border' | 'shadow')}
                >
                  <option value="border">{t('design.panelBorder')}</option>
                  <option value="shadow">{t('design.panelShadow')}</option>
                </Select>
              </Field>
              <Field label={t('design.tableHeader')}>
                <Select
                  value={theme.style.tableHeader}
                  disabled={readOnly}
                  onChange={(e) => setStyle('tableHeader', e.target.value as 'plain' | 'filled')}
                >
                  <option value="plain">{t('design.tableHeaderPlain')}</option>
                  <option value="filled">{t('design.tableHeaderFilled')}</option>
                </Select>
              </Field>
              <Field label={t('design.badge')}>
                <Select
                  value={theme.style.badge}
                  disabled={readOnly}
                  onChange={(e) => setStyle('badge', e.target.value as 'soft' | 'outline')}
                >
                  <option value="soft">{t('design.badgeSoft')}</option>
                  <option value="outline">{t('design.badgeOutline')}</option>
                </Select>
              </Field>
              <Field label={t('design.secondaryButton')}>
                <Select
                  value={theme.style.secondaryButton}
                  disabled={readOnly}
                  onChange={(e) =>
                    setStyle('secondaryButton', e.target.value as 'outline' | 'tonal')
                  }
                >
                  <option value="outline">{t('design.secondaryOutline')}</option>
                  <option value="tonal">{t('design.secondaryTonal')}</option>
                </Select>
              </Field>
              <Field label={t('design.font')}>
                <Select
                  value={theme.font}
                  disabled={readOnly}
                  onChange={(e) =>
                    setTheme({ ...theme, font: e.target.value as ThemeFile['font'] })
                  }
                >
                  {THEME_FONTS.map((f) => (
                    <option key={f} value={f}>
                      {t(`design.fonts.${f}`)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('design.menu')}>
                <Select
                  value={theme.menu}
                  disabled={readOnly}
                  onChange={(e) =>
                    setTheme({ ...theme, menu: e.target.value as ThemeFile['menu'] })
                  }
                >
                  {THEME_MENUS.map((m) => (
                    <option key={m} value={m}>
                      {t(`design.menus.${m}`)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('design.headerRule')}>
                <Select
                  value={theme.style.pageHeaderRule ? 'yes' : 'no'}
                  disabled={readOnly}
                  onChange={(e) => setStyle('pageHeaderRule', e.target.value === 'yes')}
                >
                  <option value="yes">{t('design.headerRuleYes')}</option>
                  <option value="no">{t('design.headerRuleNo')}</option>
                </Select>
              </Field>
              <Field label={t('design.name')}>
                <Input
                  value={theme.name}
                  maxLength={60}
                  disabled={readOnly}
                  onChange={(e) => setTheme({ ...theme, name: e.target.value || BASE_THEME.name })}
                />
              </Field>
            </div>
            <p className="text-sm text-muted">{t('design.fontHint')}</p>
          </Panel>
        </div>
        <Preview />
      </div>

      {!readOnly && (
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            variant="secondary"
            disabled={save.isPending || JSON.stringify(theme) === JSON.stringify(BASE_THEME)}
            onClick={() => setTheme(BASE_THEME)}
          >
            <RotateCcw size={16} aria-hidden />
            {t('design.reset')}
          </Button>
          <Button disabled={save.isPending || !dirty} onClick={() => save.mutate()}>
            {save.isPending ? t('common.saving') : t('design.save')}
          </Button>
        </div>
      )}
      {leave.dialog}
    </div>
  );
}

/** As peças do sistema, de verdade, com o tema que está na tela. */
const SAMPLE_MONEY = [
  { amount: '12480.00', currency: 'BRL' },
  { amount: '3150.90', currency: 'USD' },
  { amount: '870.00', currency: 'EUR' },
];

function Preview() {
  const { t, i18n } = useTranslation();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive' | 'all'>('active');
  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-base font-semibold">{t('design.preview')}</h2>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <StatCard label={t('design.sample.statActive')} value={12} />
        <StatCard label={t('design.sample.statSent')} value={4380} />
        <StatCard label={t('design.sample.statError')} value={2} alert />
      </div>
      <Panel
        title={t('design.sample.panel')}
        action={<Button variant="secondary">{t('design.sample.secondary')}</Button>}
      >
        <div className="flex flex-wrap items-center gap-2">
          <Button>{t('design.sample.primary')}</Button>
          <Button variant="secondary">{t('design.sample.secondary')}</Button>
          <Button variant="danger">{t('design.sample.danger')}</Button>
          <a
            href="#design"
            onClick={(e) => e.preventDefault()}
            className="text-sm text-brand-text underline"
          >
            {t('design.sample.link')}
          </a>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge tone="ok">{t('design.sample.ok')}</Badge>
          <Badge tone="info">{t('design.sample.info')}</Badge>
          <Badge tone="off">{t('design.sample.off')}</Badge>
          <Badge tone="accent">{t('design.sample.accent')}</Badge>
          <Badge>{t('design.sample.neutral')}</Badge>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('design.sample.field')} hint={t('design.sample.fieldHint')} required>
            <Input defaultValue={t('design.sample.fieldValue')} />
          </Field>
          <Field label={t('design.sample.select')}>
            <Select defaultValue="a">
              <option value="a">{t('design.sample.option')}</option>
            </Select>
          </Field>
        </div>
      </Panel>
      <ListToolbar>
        <SearchBox value={q} onChange={setQ} placeholder={t('design.sample.search')} />
        <StatusFilter value={status} onChange={setStatus} />
      </ListToolbar>
      <Table
        head={[
          t('design.sample.colName'),
          t('design.sample.colStatus'),
          { label: t('design.sample.colTotal'), align: 'right' },
        ]}
      >
        {status === 'inactive' ? (
          <EmptyRow colSpan={3}>{t('design.sample.empty')}</EmptyRow>
        ) : (
          (['row1', 'row2', 'row3'] as const).map((row, i) => (
            <tr key={row}>
              <td>{t(`design.sample.${row}`)}</td>
              <td>
                <Badge tone={(['ok', 'info', 'neutral'] as const)[i]}>
                  {t(
                    (['design.sample.ok', 'design.sample.info', 'design.sample.neutral'] as const)[
                      i
                    ]!,
                  )}
                </Badge>
              </td>
              <NumCell>{formatMoney(SAMPLE_MONEY[i]!, i18n.language)}</NumCell>
            </tr>
          ))
        )}
      </Table>
    </div>
  );
}
